import { useEffect, useState } from 'react';
import { ArchiveRestore, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { invalidateMapInit } from '@/lib/map/mapInitCache';
import type { FarmWithAggregate } from '../types';
import { useRestoreFarm } from '../hooks/useStructureQueries';

interface RestoreFarmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  farm: FarmWithAggregate | null;
}

/**
 * Восстановление хозяйства из архива (AK-340, wireframe фрейм 5). Спрашивает,
 * поднимать ли вместе с хозяйством его каскадно-архивированные поля
 * (restore_fields). Бэк (POST /farms/{id}/restore) поднимает только поля,
 * заархивированные каскадом этого архива — вручную архивированные ДО архива
 * хозяйства остаются в архиве.
 */
export function RestoreFarmDialog({
  open,
  onOpenChange,
  farm,
}: RestoreFarmDialogProps) {
  const restore = useRestoreFarm();
  const [restoreFields, setRestoreFields] = useState(true);
  const [error, setError] = useState<string | undefined>();

  // Сброс состояния при открытии на новом хозяйстве.
  useEffect(() => {
    if (open) {
      setRestoreFields(true);
      setError(undefined);
    }
  }, [open, farm?.id]);

  if (!farm) return null;

  const submit = async () => {
    setError(undefined);
    try {
      await restore.mutateAsync({ farmId: farm.id, restoreFields });
      // Поля вернулись на карту — сбрасываем chip-кеш /map.
      invalidateMapInit();
      toast.success(`Хозяйство «${farm.name}» восстановлено`);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) setError(e.detail);
      else setError('Не удалось восстановить хозяйство');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Восстановить «{farm.name}»?</DialogTitle>
          <DialogDescription>
            Хозяйство снова появится в структуре, на карте и в списках.
          </DialogDescription>
        </DialogHeader>

        <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border p-3 text-sm">
          <Checkbox
            checked={restoreFields}
            onCheckedChange={(v) => setRestoreFields(v === true)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-foreground">
              Восстановить поля тоже
            </span>
            <span className="mt-0.5 block text-muted-foreground">
              Поднимутся поля, скрытые вместе с хозяйством. Поля, которые вы
              архивировали вручную раньше, останутся в архиве.
            </span>
          </span>
        </label>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={restore.isPending}
          >
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={restore.isPending}
          >
            {restore.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Восстанавливаю…
              </>
            ) : (
              <>
                <ArchiveRestore className="mr-1.5 h-4 w-4" />
                Восстановить
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
